/*========================================================================
* $Id$
*
* Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2013
*
* Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
* Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
* Urheber festgelegten Stelle in der Funktion des Computerprogramms
* angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
* Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
* Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
* =========================================================================*/

define([
  "underscore",
  "moment",
  "utils/dom",
  "api/v2/common"
], function (
  _,
  moment,
  dom,
  Common
) {

  var SEPARATOR = "--";

  var AVAILABLE_FILTER_PREFIXES = {
    FILTER: 'filtersJson',
    LEGACY_FILTER: 'filter_',
    DATE: 'date_',
    VALUE: 'value_',
    DATA_SOURCES: "sourceContext"
  };

  var QUERY_PARAMETERS_NAME = {
    QUERY: 'query',
    QUERY_: 'query_',
    QUERY_DATE_: 'query_date_',
    ORDER_BY: 'orderby',
    ORDER_DIRECTION: 'orderDir',
    SUMMARIZE_BY_PROPERTY: 'summarizeByProp',
    SUMMARIZE_BY_ORDER_BY: 'summarizeByOrder',
    DATA_SOURCES: 'sourceContext',
    SELECTED_TAB: 'tab'
  };

  var SEARCH_MODEL_PATHS = {
    ORDER_BY: 'orderby',
    QUERY: 'user.query.and',
    ORDER_DIRECTION: 'order_direction',
    USER_CONSTRAINTS: 'user.constraints',
    VIEW: 'view',
    SOURCE_CONTEXT_FILTER_DATASOURCES: 'source_context.constraints.filter_datasources.filter_base',
    VIEW_NAME: 'source_context.constraints.view.name',
    USER_CONSTRAINTS_DATE: 'user.constraints.filter_mes:date',
    USER_CONSTRAINTS_DYNAMIC_FILTER: 'user.constraints.filter_', // dynamic filter prefix for userconstraints except date
  };

  function createQueryParamFromUrlParamFactory(queryParamValue, queryParamName, querySearchParamName) {
    var obj;
    if (queryParamName == QUERY_PARAMETERS_NAME.ORDER_BY) {
      obj = new QueryParamOrderBy();
    } else if (queryParamName == QUERY_PARAMETERS_NAME.SUMMARIZE_BY_PROPERTY) {
      obj = new QueryParamSummarizeByProp();
    } else if (queryParamName == QUERY_PARAMETERS_NAME.SUMMARIZE_BY_ORDER_BY) {
      obj = new QueryParamSummarizeByOrder();
    } else if (queryParamName == QUERY_PARAMETERS_NAME.ORDER_DIRECTION) {
      obj = new QueryParamOrderDirection();
    } else if (queryParamName == QUERY_PARAMETERS_NAME.SELECTED_TAB) {
      obj = new QueryParamSelectedTab();
    } else if (queryParamName.startsWith(AVAILABLE_FILTER_PREFIXES.FILTER) || queryParamName.startsWith(AVAILABLE_FILTER_PREFIXES.LEGACY_FILTER)) {
      obj = new FiltersQueryParam();
    } else if (queryParamName.startsWith(AVAILABLE_FILTER_PREFIXES.DATE)) {
      obj = new DateFilter();
    } else if (queryParamName.startsWith(AVAILABLE_FILTER_PREFIXES.VALUE)) {
      obj = new ValueFilter();
    } else if (queryParamName === QUERY_PARAMETERS_NAME.DATA_SOURCES) {
      obj = new QueryParamDatasources();
    } else if (queryParamName == querySearchParamName) {
      obj = new InputUserQuery(queryParamValue,querySearchParamName );
    } else if (queryParamName.startsWith(QUERY_PARAMETERS_NAME.QUERY_DATE_)) {
      obj = new DateUserQuery();
    } else if (queryParamName.startsWith(QUERY_PARAMETERS_NAME.QUERY_)) {
      obj = new RegularUserQuery();
    }

    if (obj) {
      obj.fromQueryParam(queryParamName, queryParamValue);
    }
    return obj;
  }

  function createQueryParamFilterFactory(constraint) {
    var hasAndUnit = function () {
      var base = constraint.filter_base[Object.keys(constraint.filter_base)[0]];
      return base && base.and && base.and.length > 0 && base.and[0].hasOwnProperty("unit") && base.and[0].unit === "ms_since_1970";
    };

    var hasFilterBase = function (filter) {
      return filter.filter_base && Object.keys(filter.filter_base).length > 0;
    };

    if (constraint.noFilter) {
      return new ValueFilter(constraint);
    } else if (hasFilterBase(constraint) && hasAndUnit()) {
      return new DateFilter(constraint);
    } else if (hasFilterBase(constraint)) {
      return new FiltersQueryParam(constraint);
    }
  }

  function getCurrentUrl() {
    return new URL(window.location);
  };

  function createQueryParamFactory(searchModelValue, type) {
    switch (type) {
      case QUERY_PARAMETERS_NAME.QUERY:
        return new QueryParamQuery(searchModelValue);
      case QUERY_PARAMETERS_NAME.ORDER_BY:
        return new QueryParamOrderBy(searchModelValue);
      case QUERY_PARAMETERS_NAME.ORDER_DIRECTION:
        return new QueryParamOrderDirection(searchModelValue);
      case QUERY_PARAMETERS_NAME.SUMMARIZE_BY_PROPERTY:
        return new QueryParamSummarizeByProp(searchModelValue);
      case QUERY_PARAMETERS_NAME.SUMMARIZE_BY_ORDER_BY:
        return new QueryParamSummarizeByOrder(searchModelValue);
      case QUERY_PARAMETERS_NAME.DATA_SOURCES:
        return new QueryParamDatasources(searchModelValue);
      case QUERY_PARAMETERS_NAME.SELECTED_TAB:
        return new QueryParamSelectedTab(searchModelValue);
      default:
        return;
    }
  }

  function createUserQueryFactory(query, key) {
    var hasAndUnit = function () {
      return query.and && query.and.length > 0 && query.and[0].hasOwnProperty("unit") && query.and[0].unit === "ms_since_1970";
    };
 
    if (hasAndUnit()) {
      return new DateUserQuery(query);
    }
    return new InputUserQuery(query, key);
  }

  function clearSearchModel(searchModel) {
    Object.values(SEARCH_MODEL_PATHS).forEach(function (path) {
      searchModel.unset(path);
    });
  }

  function getAll(urlSearchParams) {
    const url = getCurrentUrl();
    const params = Object.create(null);
    urlSearchParams = urlSearchParams || url.searchParams;
    for (const [key, value] of urlSearchParams.entries()) {
      if (key in params) {
        if (!Array.isArray(params[key])) {
          params[key] = [params[key]];
        }
        params[key].push(value);
      } else {
        params[key] = value;
      }
    }

    return params;
  }

  var flattenParams = function (input) {
    const result = [];

    const collect = (item) => {
      if (!item || typeof item !== 'object') return;
      if (Array.isArray(item)) return item.forEach(collect);

      if ('label' in item) {
        result.push({
          label: item.label,
          type: item.type,
          value: 'value' in item ? item.value : undefined,
        });
      }
    };

    const items = Array.isArray(input) ? input : [input];
    items.forEach(collect);

    return result;
  };

  var prepareParamValue = function (value) {
    if (value == null || value === '') return null;

    if (Array.isArray(value)) {
      // nested arrays → JSON
      if (Array.isArray(value[0])) {
        return JSON.stringify(value);
      }
      // normal array or object → JSON
      return JSON.stringify(value);
    }

    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  var groupNestedFilterPaths = function (paths) {
    const grouped = paths.reduce((memo, path) => {
      if (path.length === 0) return memo;

      const [category, hash] = path[0];
      const rest = path.slice(1);

      if (!memo[category]) {
        memo[category] = rest.length > 0
          ? { h: hash, p: [rest] }
          : { h: hash };
      } else if (memo[category].p) {
        memo[category].p.push(rest);
      } else {
        memo[category].p = [rest];
      }

      return memo;
    }, {});

    for (const key of Object.keys(grouped)) {
      const node = grouped[key];
      if (node.p?.length > 0) {
        node.p = groupNestedFilterPaths(node.p);
      }
    }

    return grouped;
  };

  function queryStringToObject(query) {
    var obj = {};
  
    if (!query) return obj;
  
    var pairs = query.split("&");
    function decode(str) {
      return decodeURIComponent(
        str.replace(/\+/g, " ")
      );
    }

    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i];
  
      var idx = pair.indexOf("=");
  
      if (idx === -1) {
        obj[decode(pair)] = "";
        continue;
      }
  
      var key = pair.substring(0, idx);
      var value = pair.substring(idx + 1);
      obj[decode(key)] = decode(value);
    }
  
    return obj;
  }
  
  var toQueryString = function (paramsInput) {
    const flatParams = flattenParams(paramsInput);
    let params = new URLSearchParams();

    const filterGroups = {};
    const dataSourceGroups = [];

    const appendJsonParam = function (paramKey, data, errorMessage, hasData) {
      if (!hasData) return;
  
      try {
        params.append(paramKey, encodeURIComponent(JSON.stringify(data)));
      } catch (err) {
        console.warn(errorMessage, err.message);
      }
    };

    const appendTabAction = function (params, value) {
      const tabName = Array.isArray(value) ? value[0] : value;
      if (!tabName) return;
      params.append("tab", tabName);
    };


    
    for (const key of Object.keys(filterGroups)) {
      filterGroups[key] = groupNestedFilterPaths(filterGroups[key]);
    }
    
    for (const { label, type, value } of flatParams) {
      if (type === AVAILABLE_FILTER_PREFIXES.FILTER) {
        if (!filterGroups[label]) {
          filterGroups[label] = [];
        }
        filterGroups[label].push(value);
        continue;
      }
  
      if (type === AVAILABLE_FILTER_PREFIXES.DATA_SOURCES) {
        dataSourceGroups.push(value);
        continue;
      }
      
      if (label === QUERY_PARAMETERS_NAME.SELECTED_TAB || type === "tab") {
        appendTabAction(params, value);
        continue;
      }

      const processed = prepareParamValue(value);
      if (processed !== null) {
        params.append(label , processed);
      }
    }
    for (const label of Object.keys(filterGroups)) {
      filterGroups[label] = groupNestedFilterPaths(filterGroups[label]);
    }
  
    appendJsonParam(
      AVAILABLE_FILTER_PREFIXES.FILTER,
      filterGroups,
      "Failed to stringify nested filters",
      Object.keys(filterGroups).length > 0
    );
  
    appendJsonParam(
      AVAILABLE_FILTER_PREFIXES.DATA_SOURCES,
      dataSourceGroups,
      "Failed to stringify data sources",
      dataSourceGroups.length > 0
    );
  
    return params.toString();
  }

  var actionHandlers = {
    changeView: function(actionObj, application) {
      return changeView(actionObj, application);
    }
  };

  function triggerActionsFromQueryParamsAsync(queryParams, application) {
    if (!queryParams || queryParams.length === 0) {
        return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var promises = [];
      
      for (var i = 0; i < queryParams.length; i++) {
        var param = queryParams[i];
        var actionObj = typeof param.getActionObject === 'function'
        ? param.getActionObject()
        : null;

        if (!actionObj) continue;
        
        try {
          var handler = actionHandlers[actionObj.action];
  
          if (handler) {
            var result = handler(actionObj, application);
            //check to be async 
            if (result && typeof result.then === "function") {
              promises.push(result);
            }
          }
  
        } catch (error) {
          console.error('Failed to handle action:', error);
        }
    }
      
    if (promises.length > 0) {
      Promise.all(promises).then(resolve);
    } else {
      resolve();
    }
    });
  }

  function changeView(actionObj, application) {
    var modelToWaitFor = application.models ? application.models.userSourceInfo : null;
    var tab = application.models?.tabs?.getByName(actionObj.name);
    var shouldLoadDataSources = application.options.enableUserSourceInfo && !tab && application.models.tabs.areDataSourceTabsUsed();
    return new Promise(function (resolve) {
      try {
        if (shouldLoadDataSources && modelToWaitFor) {
          application.models.profile.updatedPromise.finally(function() {
            application.waitForDataSources(modelToWaitFor, function () {
              application.changeView({
                name: actionObj.name
              });
              resolve();
            });
        });   
        } else {
          application.changeView({
            name: actionObj.name
          });
          resolve();
        }
  
      } catch (error) {
        console.error('Failed to handle action:', error);
        resolve();
      }
    });
  }
  function shouldInitFromQueryParams(searchParams, options) {
    if (!searchParams) {
        return false;
    }

    const initFromQueryParams = searchParams.get("initFromQueryParams") === "true";

    if (options?.isParamsUpdateRequired) {
        return true;
    }

    if ( options.requiresInitFromQueryParamsForAllParams !== false) {
      return initFromQueryParams
    }


    return false;
  }

  //USED IN STACK.JS
  function setUrlParams(paramsInput, merge = false, options = {}) {
    const { replace = false } = options;
    const url = new URL(getCurrentUrl());
    let params = new URLSearchParams(url.search);
    let didChange = false;

    const flatParams = flattenParams(paramsInput);

    if (merge) {
      const keysBeingSet = new Set(flatParams.map(p => p.label));

      for (const key of keysBeingSet) {
        if (params.has(key)) {
          params.delete(key);
          didChange = true;
        }
      }

      for (const { label: key, value } of flatParams) {
        const processed = prepareParamValue(value);
        if (processed !== null) {
          params.append(key, processed);
          didChange = true;
        }
      }
    } else {
      params = new URLSearchParams();

      let filterGroups = flatParams
        .filter(p => p.type === AVAILABLE_FILTER_PREFIXES.FILTER)
        .reduce((memo, { label: id, value }) => {
          if (!memo[id]) {
            memo[id] = [];
          }
          memo[id].push(value);
          return memo;
        }, {});

      for (const key of Object.keys(filterGroups)) {
        filterGroups[key] = groupNestedFilterPaths(filterGroups[key]);
      }
      for (const { label: key, type, value } of flatParams) {
        if (type === AVAILABLE_FILTER_PREFIXES.FILTER) continue;

        const processed = prepareParamValue(value);
        if (processed !== null) {
          params.append(key, processed);
          didChange = true;
        }
      }

      if (Object.keys(filterGroups).length > 0) {
        try {
          const json = JSON.stringify(filterGroups);
          params.append(AVAILABLE_FILTER_PREFIXES.FILTER, json);
          didChange = true;
        } catch (err) {
          console.warn("Failed to stringify nested filters", err.message);
        }
      }
    }

    if (didChange) {
      url.search = params.toString();
      const method = replace ? 'replaceState' : 'pushState';
      history[method](null, '', url.href);
    }
  }


  var BaseQueryParameter = function (searchModelValue) {
    this.searchModelValue = searchModelValue;
    this.separator = SEPARATOR || ",";
    this.path = '';
    this.queryParamName = '';
  }

  _.extend(BaseQueryParameter.prototype, {
    toQueryParam: function () {
      var value = null;
      if (this.searchModelValue) {
        value = this.addUrlParameter(
          _.map(this.searchModelValue.filter_base, function (filterValue) {
            return filterValue.id;
          })
        );
      }
      return {
        label: this.getQueryParamLabel(),
        value: value
      }
    },

    getQueryParamLabel: function () {
      return this.queryParamName;
    },

    addUrlParameter: function (filterValues) {
      return filterValues.join(this.separator);
    },

    setPath: function (path) {
      this.path = path;
    },

    fromQueryParam: function (parameterName, parameterValue) {
      var separatorRegex = new RegExp("\\s*\\" + this.separator + "\\s*");
      var value = _.map(parameterValue.split(separatorRegex), function (val) {
        return decodeURIComponent(val);
      });
      var cleanupFilterPrefixes = Object.values(AVAILABLE_FILTER_PREFIXES).join('|');
      this.label = parameterName.replace(new RegExp('^(' + cleanupFilterPrefixes + ')'), '');
      this.value = value;
    },

    getSeparator: function () {
      return this.separator;
    },

    getSearchModelAttrValue: function () {
      return this.value;
    },
    clearSearchModel: function (searchModel) {
      searchModel.set(this.path, undefined);
    },

    updateSearchModel: function (searchModel) {
      var SearchModelItemValue = this.getSearchModelAttrValue(searchModel)
      searchModel.set(this.path, SearchModelItemValue);
    },
  });

  // All the item in search model except user.constraints
  var QueryParameter = function () { }
  _.extend(QueryParameter.prototype, BaseQueryParameter.prototype, {
    toQueryParam: function () {
      return {
        label: this.queryParamName,
        value: this.searchModelValue
      }
    },
    fromQueryParam: function (parameterName, parameterValue) {
      this.label = parameterName
      this.value = parameterValue;
    },

    updateSearchModel: function (searchModel) {
      if (this.getSearchModelAttrValue()) {
        var queryParamValue = this.getSearchModelAttrValue()
        searchModel.set(this.path, queryParamValue);
      }
    }
  })

  var QueryParamOrderBy = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = SEARCH_MODEL_PATHS.ORDER_BY;
    this.queryParamName = QUERY_PARAMETERS_NAME.ORDER_BY;
  }

  _.extend(QueryParamOrderBy.prototype, QueryParameter.prototype);

  var QueryParamSummarizeByProp = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = SEARCH_MODEL_PATHS.SUMMARIZE_BY_PROPERTY;
    this.queryParamName = QUERY_PARAMETERS_NAME.SUMMARIZE_BY_PROPERTY;
  }

  _.extend(QueryParamSummarizeByProp.prototype, QueryParameter.prototype);

  var QueryParamSummarizeByOrder = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = SEARCH_MODEL_PATHS.SUMMARIZE_BY_ORDER_BY;
    this.queryParamName = QUERY_PARAMETERS_NAME.SUMMARIZE_BY_ORDER_BY;
  }

  _.extend(QueryParamSummarizeByOrder.prototype, QueryParameter.prototype);

  var QueryParamOrderDirection = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = SEARCH_MODEL_PATHS.ORDER_DIRECTION;
    this.queryParamName = QUERY_PARAMETERS_NAME.ORDER_DIRECTION;
  }

  _.extend(QueryParamOrderDirection.prototype, QueryParameter.prototype);

  var QueryParamSelectedTab  = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = SEARCH_MODEL_PATHS.VIEW_NAME;
    this.queryParamName = QUERY_PARAMETERS_NAME.SELECTED_TAB;
  }

  _.extend(QueryParamSelectedTab.prototype, QueryParameter.prototype, {

    updateSearchModel: function (searchModel) {
      var selectedTabName = this.getSearchModelAttrValue();
      searchModel.set(this.path, selectedTabName);
    },
    getActionObject:function(){
      return {
        action: "changeView",
        name: this.value
      }
    }
  });

  var QueryParamDatasources = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = SEARCH_MODEL_PATHS.SOURCE_CONTEXT_FILTER_DATASOURCES;
    this.queryParamName = QUERY_PARAMETERS_NAME.DATA_SOURCES;
  }

  var QueryParamQuery = function (constraint) {
    BaseQueryParameter.call(this, constraint);
    this.path = '';
    this.queryParamName = QUERY_PARAMETERS_NAME.QUERY;
  }

  _.extend(QueryParamQuery.prototype, QueryParameter.prototype, {

    getQueryPrefix: function () {
      return this.prefix;
    },

    getQueryParamLabel: function () {
      return this.getQueryPrefix();
    },

    toQueryParam: function () {
      var value = null;
      if (this.searchModelValue) {
        value = this.searchModelValue.unparsed || this.searchModelValue.description;
      }
      return {
        label: this.getQueryParamLabel() + (this.searchModelValue.label || ''),
        value: value
      }
    },

    updateSearchModel: function (searchModel) {
      var queryValue = this.getSearchModelAttrValue();
      if (queryValue && this.path) {
        searchModel.set(this.path, queryValue);
      }
    },
    

  });

  function InputUserQuery(query, searchParamsName) {
    BaseQueryParameter.call(this, query);
    this.prefix = searchParamsName;
  }

  _.extend(InputUserQuery.prototype, QueryParamQuery.prototype, {
    updateSearchModel: function (searchModel, getUnparsedUserQueryPath) {
      var queryValue = this.getSearchModelAttrValue();
      if (queryValue && typeof getUnparsedUserQueryPath === 'function' ) {
        searchModel.set(getUnparsedUserQueryPath().join("."), queryValue);
      }
    }
  })

  function RegularUserQuery(query) {
    BaseQueryParameter.call(this, query);
    this.prefix = QUERY_PARAMETERS_NAME.QUERY_
  }
  _.extend(RegularUserQuery.prototype, QueryParamQuery.prototype, {

    getSearchModelAttrValue: function () {
      var itemName = '';
      if (this.label.startsWith(this.prefix)) {
        itemName = this.label.replace(this.prefix, '');
        this.setPath(SEARCH_MODEL_PATHS.QUERY + "." + itemName);
      }

      var regular_user_query = {
        label: itemName,
        id: itemName,
        unparsed: ""
      };
      if (this.value.length) {
        regular_user_query.unparsed = this.value[0];
      }

      return regular_user_query;
    },
  })

  function DateUserQuery(query) {
    BaseQueryParameter.call(this, query);
    this.prefix = QUERY_PARAMETERS_NAME.QUERY_DATE_
  }

  _.extend(DateUserQuery.prototype, QueryParamQuery.prototype, {
    getSearchModelAttrValue: function () {
      var itemName = '';
      if (this.label.startsWith(this.prefix)) {
        itemName = this.label.replace(this.prefix, '');
        this.setPath(SEARCH_MODEL_PATHS.QUERY + "." + itemName)
      }
      var regular_user_query = {}
      if (this.value) {
        dom.updateDateInputUI(this.value, itemName);
        regular_user_query = this.createDynamicDateObject(this.value, itemName);
      }
      return regular_user_query;
    },

    createDynamicDateObject: function (dateRange, itemName) {
      var dateMatches = dateRange.match(/(\d{2}\.\d{2}\.\d{4})/g);
      var hasFrom = dateRange.indexOf('From') !== -1;
      var hasTo = dateRange.indexOf('To') !== -1;

      const resultObject = {
        label: itemName || "mes:date",
        and: [],
        description: dateRange,
        id: itemName || "mes:date"
      };

      if (dateMatches) {
        if (hasFrom) {
          var fromDateString = dateMatches[0];
          var fromDateParts = fromDateString.split('.');
          var fromDate = new Date(fromDateParts[2], fromDateParts[1] - 1, fromDateParts[0]);
          var fromTimestamp = fromDate.getTime();

          resultObject.and.push({
            num: fromTimestamp,
            cmp: "GE",
            unit: "ms_since_1970"
          });
        }

        if (hasTo && dateMatches.length > 1) {
          var toDateString = dateMatches[1];
          var toDateParts = toDateString.split('.');
          var toDate = new Date(toDateParts[2], toDateParts[1] - 1, toDateParts[0]);

          toDate.setHours(23, 59, 59, 999);
          var toTimestamp = toDate.getTime();

          resultObject.and.push({
            num: toTimestamp,
            cmp: "LE",
            unit: "ms_since_1970"
          });
        }
      }

      return resultObject;
    },
  })

  // All the item in user.constraints
  var BaseFilterQueryParam = function () { };
  _.extend(BaseFilterQueryParam.prototype, BaseQueryParameter.prototype, {
    getFilterPrefix: function () { return '' },
    getQueryParamLabel: function () {
      return this.getFilterPrefix() + this.searchModelValue.label;
    }
  })

  
  _.extend(QueryParamDatasources.prototype, BaseQueryParameter.prototype, {
    
    fromQueryParam: function (queryParamName, queryParamValue) {
      this.paramName = queryParamName;
      try {
        this.value = JSON.parse(queryParamValue);
      } catch (error) {
        this.value = queryParamValue;
      }
    },
    buildFilterBaseFromSourceContext: function (selectedSources) {
      var filterBase = {};
      if (!_.isArray(selectedSources)) {
        return filterBase;
      }
      _.each(selectedSources, function (sourceItem) {
        if (!sourceItem || !sourceItem.id || !_.isArray(sourceItem.value)) {
          return;
        }
        var and = [];
        _.each(sourceItem.value, function (entry) {
          var rawValue = entry && entry[0];
          var label = entry && entry[1] ? entry[1] : null;
  
          if (typeof rawValue !== "string") {
            return;
          }
  
          var item = {
            label: label
          };
  
          if (rawValue.indexOf("^\\Q") === 0) {
            item.regex = rawValue;
          } else {
            item.quoted_term = rawValue;
          }
          and.push(item);
        });
  
        if (and.length > 0) {
          filterBase[sourceItem.id] = {
            and: and,
            id: sourceItem.id
          };
        }
      });
  
      return filterBase;
    },
  
    updateSearchModel: function (searchModel) {
      var selectedSources = this.getSearchModelAttrValue();
      if (!selectedSources) {
        return;
      } 
      if (typeof selectedSources === "string") {
        try {
          selectedSources = JSON.parse(decodeURIComponent(selectedSources));
        } catch (err) {
          console.warn("Failed to parse sourceContext from URL", err.message);
          return;
        }
      }
    
      var matchedSources = this.buildFilterBaseFromSourceContext(selectedSources);
      var sourceContext = _.clone(searchModel.input.get("source_context")) || {};
      if (!sourceContext.constraints || typeof sourceContext.constraints !== "object" || _.isArray(sourceContext.constraints)) {
        sourceContext.constraints = {};
      }
      var filter_datasources = {
        id: "filter_datasources",
        label: "datasources",
        filtered_name: "Sources",
        filtered: {},
        filter_base: matchedSources
      };
      sourceContext.constraints.filter_datasources = filter_datasources;
      searchModel.input.set("source_context", sourceContext);
      
    },
  
    toQueryParam: function () {
      var branches = [];
      var filterBase = this.searchModelValue && this.searchModelValue.filter_base;
  
      if (!filterBase || typeof filterBase !== "object") {
        return [];
      }
  
      _.each(filterBase, function (datasource) {
        if (!datasource || !_.isArray(datasource.and)) {
          return;
        }
        var path = [];
        _.each(datasource.and, function (item) {
          if (!item || typeof item !== "object") {
            return;
          }
          var value = null;
          if (typeof item.quoted_term === "string") {
            value = item.quoted_term;
          } else if (typeof item.regex === "string") {
            value = item.regex;
          }
  
          if (value) {
            path.push([
              value,
              item.label || null
            ]);
          }
        });
  
        if (path.length > 0) {
          branches.push({
            id: datasource.id || null,
            value: path
          });
        }
      });
  
      return _.map(branches, function (branch) {
        return {
          label: this.searchModelValue.label || "datasources",
          type: "sourceContext",
          value: branch
        };
      }, this);
    }
  });

  function DateFilter(constraint) {
    BaseQueryParameter.call(this, constraint);
  }

  _.extend(DateFilter.prototype, BaseFilterQueryParam.prototype, {
    getFilterPrefix: function () {
      return AVAILABLE_FILTER_PREFIXES.DATE;
    },

    getSearchModelAttrValue: function () {
      var filterName = this.label;
      var filterQuery = "[data-name='" + filterName + "']";
      var filterLabel = 'Date';
      var filterNameWithPrefix = "filter_" + filterName
      this.setPath("user.constraints." + filterNameWithPrefix)
      var element = document.querySelector(filterQuery);
      if (element) {
        filterLabel = element.getAttribute("data-title") !== null
          ? element.getAttribute("data-title")
          : filterName;
      } else {
        filterLabel = filterName;
      }

      var DATE_FILTERS = _.map(this.value, function (element) {
        var filter_strings = element.split(".");
        return { "year": filter_strings[0], "month": filter_strings[1] };
      });
      var date_filter_structure = { label: filterName, filtered_name: filterLabel, filtered: {}, filter_base: {}, id: filterNameWithPrefix };
      var that = this;
      _.each(DATE_FILTERS, function (filterEntry) {
        if (filterEntry.month !== undefined) {
          var year_month = filterEntry.year + "." + filterEntry.month;
          date_filter_structure.filter_base[year_month] = that.createHierarchicalDateFilter(filterEntry.month, filterEntry.year, filterName);
        } else {
          _.each(that.value, function (filter_value) {
            date_filter_structure.filter_base[filter_value] = that.createNoneHierarchicalDateFilterYearEntry(filter_value, filterName);
          });
        }
      });
      return date_filter_structure;
    },

    getYearTimestamps: function (year) {
      var startOfYear = moment().year(year).startOf("year").unix() * 1000;
      var endOfYear = moment().year(year).endOf("year").unix() * 1000;
      return {
        startOfYear: startOfYear,
        endOfYear: endOfYear
      };
    },

    getMonthTimestamps: function (year, month) {
      var startOfMonth = moment().year(year).month(month).startOf("month").toDate().getTime();
      var endOfMonth = moment().year(year).month(month).endOf("month").toDate().getTime();
      return {
        startOfMonth: startOfMonth,
        endOfMonth: endOfMonth
      };
    },

    getTimestamps: function (year, month) {
      if (month) {
        return this.getMonthTimestamps(year, month);
      } else {
        return this.getYearTimestamps(year);
      }
    },

    createNoneHierarchicalDateFilterYearEntry: function (year, label) {
      var timestamps = this.getTimestamps(year);
      var startOfYear = timestamps.startOfYear;
      var endOfYear = timestamps.endOfYear;

      return {
        label: label,
        and: [
          {
            num: startOfYear,
            cmp: "GE",
            unit: "ms_since_1970"
          },
          {
            num: endOfYear,
            cmp: "LE",
            unit: "ms_since_1970"
          }
        ],
        description: year,
        id: year,
        value: {
          num: startOfYear,
          unit: "ms_since_1970"
        },
        path: []
      };
    },

    createHierarchicalDateFilter: function (month, year, label) {
      var timestamps = this.getTimestamps(year, month);
      var startOfMonth = timestamps.startOfMonth;
      var endOfMonth = timestamps.endOfMonth;
      var yearMonthLabel = year + "." + month;

      return {
        label: label,
        and: [
          {
            num: startOfMonth,
            cmp: "GE",
            unit: "ms_since_1970"
          },
          {
            num: endOfMonth,
            cmp: "LE",
            unit: "ms_since_1970"
          }
        ],
        description: month,
        id: yearMonthLabel,
        value: {
          num: startOfMonth,
          unit: "ms_since_1970"
        },
        path: [
          {
            label: label,
            and: [
              {
                num: startOfMonth,
                cmp: "GE",
                unit: "ms_since_1970"
              },
              {
                num: endOfMonth,
                cmp: "LE",
                unit: "ms_since_1970"
              }
            ],
            description: year,
            id: year,
            value: {
              num: startOfMonth,
              unit: "ms_since_1970"
            },
            path: []
          }
        ]
      };
    }
  });

  function FiltersQueryParam(constraint) {
    BaseQueryParameter.call(this, constraint);
  }

  _.extend(FiltersQueryParam.prototype, BaseFilterQueryParam.prototype, {

    toQueryParam: function () {
      const branches = [];

      if (!this.searchModelValue || !this.searchModelValue.filter_base) {
        return [];
      }

      Object.values(this.searchModelValue.filter_base).forEach(filter => {
        const path = [];
        // in case of nested filter collect paths
        filter.path?.forEach(ancestor => {
          let value = Common.Properties.parseValue(ancestor.value);
          if (typeof value === "string") {
            path.push([value, ancestor.hash]);
          } else {
            console.warn(`${ancestor.value} is not supported in query parameters!`);
          }
        });
        let value = Common.Properties.parseValue(filter.value);
        if (typeof value === "string") {
          path.push([value, filter.hash]);
        } else {
          console.warn(`${filter.value} is not supported in query parameters!`);
          return;
        }

        if (path.length > 0) {
          branches.push(path);
        }
      });


      const flatValue = branches.flatMap(value => { return { label: this.searchModelValue.label, type: AVAILABLE_FILTER_PREFIXES.FILTER, value } });
      return flatValue
    },

    fromQueryParam: function (queryParamName, queryParamValue) {
      this.paramName = queryParamName;
      try {
        this.value = JSON.parse(queryParamValue);
      } catch (error) {
        this.value = queryParamValue;
      }
    },

    addUrlParameter: function (filterValues) {
      return filterValues.join(this.separator);
    },

    getFilterPrefix: function () {
      return AVAILABLE_FILTER_PREFIXES.FILTER;
    },

    getSearchModelAttrValue: function () {
      return this.value || null;
    },

    adaptLegacyFilterToNewSchema: function (filterName, params) {
      const splited = params.split(SEPARATOR);
      const filter = filterName.split("_")[1];
      const parsed = {};
      const selectedFilter = splited.reduce((acc, label) => {
        acc[label] = {};
        return acc;
      }, {});
      parsed[`${filter}`] = selectedFilter;
      return parsed;
    },

    updateSearchModel: function (searchModel) {
      let paramValue = this.getSearchModelAttrValue();
      let parsedFilter = {};

      if (!paramValue) {
        return;
      }

      //backward compatible with old filters
      if (this.paramName.startsWith("filter_")) {
        parsedFilter = this.adaptLegacyFilterToNewSchema(this.paramName, paramValue);
      } else {
        parsedFilter = JSON.parse(decodeURIComponent(paramValue));
      }

      if (Object.keys(parsedFilter).length === 0) return;

      const constraints = this.buildFilterConstraints(parsedFilter);

      if (Object.keys(constraints).length > 0) {
        for (let key of Object.keys(constraints)) {
          searchModel.input.submodel("user.constraints").set(`filter_${key}`, constraints[key]);
        }
      }
    },

    /**
    * Converts raw facet data (hierarchical + flat) into normalized UI filter structure.
    *
    * @param {Object} data - Raw filter input with categories, classes, extensions etc.
    * @returns {Object} Normalized filter definitions ready for UI components
    *
    * @example
    * Input: { Category: { Fachartikel: { h: "...", p: { "Trends 2022": { h: "..." } } } }, ... }
    * Output: { Category: { label: "Category", id: "filter_Category", filter_base: [...] }, ... }
    */
    buildFilterConstraints: function (data) {
      const nodes = new Map();

      const buildFilterBase = (category, parents, filters, currentLevel) => {
        for (const label of Object.keys(filters)) {
          const hash = filters[label].h || undefined;

          if (nodes.has(label)) {
            const node = nodes.get(label);
            node.path = [...node.path, ...parents];
          } else {
            let newNode;

            if (hash) {
              const parentId = parents.length ? parents[parents.length - 1].id : '';
              const idStr = label;
              const nodeId = parentId ? `${parentId}.${idStr}` : idStr;

              newNode = {
                label: `${category}@${currentLevel}`,
                regex: `^\\Q${label}\\E$`,
                description: label,
                id: nodeId,
                value: { str: label },
                hash: hash || '',
                path: [...parents]
              };
            } else if (parents.length === 0) {
              newNode = {
                label: category,
                regex: `^\\Q${label}\\E$`,
                description: label,
                id: label,
                value: { str: label }
              };
            } else {
              continue;
            }

            if (filters[label].p) {
              buildFilterBase(category, [...parents, newNode], filters[label].p, currentLevel + 1);
            } else {
              nodes.set(label, newNode);
            }
          }
        }
      };

      for (const category of Object.keys(data)) {
        buildFilterBase(category, [], data[category], 0);
      }

      const constraints = {};

      for (const node of nodes.entries()) {
        const category = node[1].label.split("@")[0];
        if (!constraints[category]) {
          constraints[category] = {
            label: category,
            id: `filter_${category}`,
            filtered_name: category,
            filter_base: []
          };
        }
        constraints[category].filter_base.push(node[1]);
      }

      return constraints;
    }

  });

  function ValueFilter(constraint) {
    BaseQueryParameter.call(this, constraint);
  }

  _.extend(ValueFilter.prototype, BaseFilterQueryParam.prototype, {
    getFilterPrefix: function () {
      return AVAILABLE_FILTER_PREFIXES.VALUE;
    },
    getSearchModelAttrValue: function (searchModel) {
      var filterName = decodeURIComponent(this.value.replace(AVAILABLE_FILTER_PREFIXES.VALUE, ""));
      searchModel.setSelectedFacetValues(filterName, this.value, filterName);
      searchModel.set(SEARCH_MODEL_PATHS.USER_CONSTRAINTS_DYNAMIC_FILTER + filterName + ".noFilter", true);
    }
  });

  return {
    toQueryString: function (searchModel, querySearchParamName) {
      querySearchParamName = querySearchParamName || 'query';
      var queryParameterList = [];
      var sourceContext = null;
      var sourceContextData = searchModel.input.get('source_context');
      if (sourceContextData && sourceContextData.constraints && sourceContextData.constraints.view) {
        sourceContext = sourceContextData.constraints.view.name;
      }
      var userQueries = searchModel.input.get(SEARCH_MODEL_PATHS.QUERY);
      _.forEach(userQueries, function (userQuery) {
        queryParameterList.push(createUserQueryFactory(userQuery, querySearchParamName));
      })
      queryParameterList.push(createQueryParamFactory(sourceContext, QUERY_PARAMETERS_NAME.SELECTED_TAB));

      var userConstraints = searchModel.input.get('user.constraints') || [];
      Object.keys(userConstraints).forEach(function (constraint) {
        queryParameterList.push(createQueryParamFilterFactory(userConstraints[constraint]));
      });
      queryParameterList.push(createQueryParamFactory(searchModel.input.get('orderby'), QUERY_PARAMETERS_NAME.ORDER_BY));
      queryParameterList.push(createQueryParamFactory(searchModel.input.get('order_direction'), QUERY_PARAMETERS_NAME.ORDER_DIRECTION));

      var summarizeBy = searchModel.input.get('summarize_by');
      if (summarizeBy) {
        queryParameterList.push(createQueryParamFactory(summarizeBy.orderby, QUERY_PARAMETERS_NAME.SUMMARIZE_BY_ORDER_BY));
        queryParameterList.push(createQueryParamFactory(summarizeBy.property, QUERY_PARAMETERS_NAME.SUMMARIZE_BY_PROPERTY));
      }
      if (sourceContextData && sourceContextData.constraints && sourceContextData.constraints.filter_datasources) {
        var filterBase = sourceContextData.constraints.filter_datasources || {};
        queryParameterList.push(createQueryParamFactory(filterBase, QUERY_PARAMETERS_NAME.DATA_SOURCES));
      }
      queryParameterList = _.filter(queryParameterList, function (queryParamItem) { return !!queryParamItem })
      var params = queryParameterList.map(function (queryParam) {
        return queryParam.toQueryParam();
      });
      return toQueryString(params);
    },

    triggerActionsFromQueryParamsAsync: triggerActionsFromQueryParamsAsync,
    getAllQueryParamsFromSearchParams: function (searchParams, options) {
      options = Object.assign({}, {
          requiresInitFromQueryParamsForAllParams: true,
          querySearchParamName: "query"
      }, (options || {}));
  
      if (!searchParams) {
          return [];
      }
      var shouldInitAllParams = shouldInitFromQueryParams(searchParams , options)
      
      var urlParametersObject = {};
      searchParams.forEach(function (value, key) {
          urlParametersObject[key] = value;
      });
      const querySearchParamName = options.querySearchParamName
      
      if (!shouldInitAllParams) {
          var queryName = options.querySearchParamName;
          var onlyQuery = {};
  
          if (urlParametersObject.hasOwnProperty(queryName)) {
              onlyQuery[queryName] = urlParametersObject[queryName];
          }
          urlParametersObject = onlyQuery;
      }
  
      return _.map(urlParametersObject, function (parameterValue, parameterName) {
        return createQueryParamFromUrlParamFactory(
            parameterValue,
            parameterName,
            querySearchParamName
        );
      }).filter(function (item) {
          return item != null;
      });
    },

    updateSearchModelFromQueryParams: function(queryParams, searchModelInput,getUnparsedUserQueryPath) {
      for (const param of queryParams) {
          param.updateSearchModel(searchModelInput, getUnparsedUserQueryPath);
       }   
    },

    resetSelectedParams: function () {
      var url = getCurrentUrl();
      var paramsToRemove = Object.values(AVAILABLE_FILTER_PREFIXES).filter(function (prefix) {
        return prefix && prefix.length > 0;
      });
      var searchParams = new URLSearchParams(url.searchParams.toString());
      var keys = Array.from(searchParams.keys());

      _.each(keys, function (key) {
        var decodedKey = decodeURIComponent(key);
        if (_.some(paramsToRemove, function (prefix) {
          return decodedKey.startsWith(prefix);
        })) {
          searchParams.delete(key);
        }
      });
      url.search = searchParams.toString();
      history.pushState({}, "", url.href);
    },

    setUrlParams: setUrlParams,

    groupNestedFilterPaths: groupNestedFilterPaths,

    FiltersQueryParam: FiltersQueryParam,

    queryStringToObject: queryStringToObject,
    shouldInitFromQueryParams: shouldInitFromQueryParams
    
  }
})


